import { createSelector, Selector } from 'reselect';

import { APICallStatus } from '../../design-system/types';
import { RootState } from '../../shared/redux/types';
import { DossierId } from '../dossier/types';

import {
    Discussion,
    NormalizedSendingMessageStatus,
    SendingMessage,
    SendingMessageStatus,
    DraftMessageMetadata,
} from './types';

export const getDraftMessage =
    (dossierId: DossierId) =>
    (
        state: RootState,
    ): {
        draftMessage: string;
        htmlDraftMessage: string;
        draftMessageMetadata?: DraftMessageMetadata;
    } => ({
        draftMessage: state.entities.discussion[dossierId]?.draftMessage || '',
        htmlDraftMessage: state.entities.discussion[dossierId]?.htmlDraftMessage || '',
        draftMessageMetadata: state.entities.discussion[dossierId]?.draftMessageMetadata,
    });

export const getSendingMessageStatus =
    (dossierId: DossierId) =>
    (state: RootState): NormalizedSendingMessageStatus | null | undefined =>
        state.status.discussion[dossierId]?.sendingMessage;

const getSendingMessageStatusById =
    (dossierId: DossierId, sendingMessageId: string) =>
    (state: RootState): SendingMessageStatus =>
        state.status.discussion[dossierId]?.sendingMessage[sendingMessageId] ||
        ({} as SendingMessageStatus);

export const getDraftMessageStatus =
    (dossierId: DossierId) =>
    (state: RootState): APICallStatus | undefined =>
        state.status.discussion[dossierId]?.getDraftMessage;

export const saveDraftMessageStatus =
    (dossierId: DossierId) =>
    (state: RootState): APICallStatus | undefined =>
        state.status.discussion[dossierId]?.saveDraftMessage;

export const updateMessageStatus =
    (dossierId: DossierId) =>
    (state: RootState): APICallStatus | undefined =>
        state.status.discussion[dossierId]?.updateMessage;

export const getContentStatusBySendingMessageId = (
    dossierId: DossierId,
    sendingMessageId: string,
): Selector<RootState, APICallStatus | undefined> =>
    createSelector(
        getSendingMessageStatusById(dossierId, sendingMessageId),
        (sendingMessageStatus) => sendingMessageStatus.postContent,
    );

export const getSendingMessageIdsByDossierId =
    (dossierId: DossierId) =>
    (state: RootState): Array<string> =>
        Object.keys(state.entities.discussion[dossierId]?.sendingMessage || {}).filter(Boolean);

export const getPendingOrErrorSendingMessageIdsByDossierId = (
    dossierId: DossierId,
): Selector<RootState, Array<string>> =>
    createSelector(
        getSendingMessageIdsByDossierId(dossierId),
        getSendingMessageStatus(dossierId),
        (sendingMessageIds, sendingMessageStatuses) =>
            sendingMessageIds.filter(
                (sendingMessageId) => sendingMessageStatuses?.[sendingMessageId]?.postContent,
            ),
    );

export const getSendingMessageById =
    (dossierId: DossierId, sendingMessageId: string) =>
    (state: RootState): SendingMessage | undefined => {
        return state.entities.discussion[dossierId]?.sendingMessage?.[sendingMessageId];
    };

export const getDiscussion =
    (dossierId: DossierId) =>
    (state: RootState): Omit<Discussion, 'messages' | 'pagination'> =>
        state.entities.discussion[dossierId] || {};
